/**
 * Login Page
 *
 * Public page with login form.
 * Redirects to dashboard (or returnUrl) after successful login.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, Zap, Users } from 'lucide-react';
import { LoginForm } from '../../components/auth/LoginForm.js';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get return URL from location state (set by ProtectedRoute)
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleLoginSuccess = () => {
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="grid md:grid-cols-2 gap-0 bg-background rounded-2xl shadow-2xl overflow-hidden">
          {/* Left Side - Branding */}
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 p-12 flex flex-col justify-center text-white">
            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <img src="/colliers-logo.svg" alt="Colliers" className="h-14" />
                <div>
                  <h1 className="text-3xl font-bold">Colliers Idaho Intelligence</h1>
                  <p className="text-blue-200 text-sm">Commercial Real Estate Data Platform</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Multi-Source Data Intelligence</h3>
                    <p className="text-sm text-blue-200">Property data from brokers, public records, and third-party providers</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Zap className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Comp Analysis</h3>
                    <p className="text-sm text-blue-200">Comparable property search with confidence scoring</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Users className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Role-aware Dashboard</h3>
                    <p className="text-sm text-blue-200">Brokers, researchers, and admins each land on a layout tuned to what they actually do</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="p-12 flex items-center justify-center">
            <div className="w-full max-w-md space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-3xl font-bold tracking-tight">Welcome Back</h2>
                <p className="text-muted-foreground">Sign in to your account to continue</p>
              </div>

              <LoginForm onSuccess={handleLoginSuccess} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
